import { Button, Card, Checkbox, Flex, Grid, Group, Table, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCloudUpload, IconPlus, IconSearch, IconUser } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { codes as countriesAndCodes } from 'country-calling-code';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import Metadata from '../../components/Metadata';
import ProfileImportModal from '../../components/Modals/ProfileImportModal';
import Pager from '../../components/Pager/Pager';
import ProfileRows from '../../components/Profiles/ProfileRows';
import PageSizeOptions from '../../constants/pageSizeOptions';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const fetchDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, filter, perPage) =>
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/profiles?size=${perPage}&${queryString.stringify(filter)}`
    ),
  300
);

const ProfilesPage = () => {
  const { domain } = useSelector((state) => state.domain);
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const showImportProfilesModal = searchParams.get('showImportProfilesModal');
  const { accountId, domainId } = useParams();

  const [filter, setFilter] = useState({ from: 0, showAnonymous: false });
  const [profiles, setProfiles] = useState();
  const [totalCount, setTotalCount] = useState();
  const [pageSize, setPerPage] = useState(PageSizeOptions[0]);
  const [pageCount, setPageCount] = useState();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [phoneValidationError, setPhoneValidationError] = useState();
  const countryPhoneCode =
    countriesAndCodes.find((c) => c.country.toLowerCase() === domain?.country?.toLowerCase())?.countryCodes?.[0] || '1';

  useEffect(() => {
    const parsedSearch = queryString.parse(location.search);
    setFilter({
      ...parsedSearch,
      from: parseInt(parsedSearch.from || '0'),
      showAnonymous: parsedSearch.showAnonymous === 'true',
    });
    setPerPage(parsedSearch.pageSize || PageSizeOptions[0]);
    setProfiles(undefined);
    fetchDebounced(fetch, accountId, domainId, queryString.parse(location.search), pageSize).then(
      ({ profiles, totalCount }) => {
        setProfiles(profiles);
        setTotalCount(totalCount);
        setPageCount(Math.ceil(totalCount / pageSize));
      }
    );
  }, [accountId, domainId, fetch, location, pageSize]);
  useEffect(() => {
    setImportModalOpen(Boolean(showImportProfilesModal));
  }, [showImportProfilesModal]);

  const validatePhoneNumber = (phoneNumber) => {
    setPhoneValidationError();

    const phoneNumberNoWhitespace = phoneNumber.replace(/\s+/g, '');

    if (!phoneNumberNoWhitespace.startsWith(`+`) || phoneNumberNoWhitespace.charAt(1) === '0') {
      setPhoneValidationError({
        message: `Phone number should start with + followed by a country phone code! Example:  +${countryPhoneCode}.`,
      });
    } else {
      return phoneNumberNoWhitespace;
    }
  };

  const search = (e) => {
    e.preventDefault();

    if (!filter.phoneNumber) {
      navigate({ search: queryString.stringify({ ...filter }) });
    } else {
      const validPhoneNumber = validatePhoneNumber(filter.phoneNumber);
      if (validPhoneNumber) {
        navigate({ search: queryString.stringify({ ...filter, phoneNumber: validPhoneNumber }) });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFilter = { ...filter, [name]: value, from: 0 };

    setFilter(newFilter);
  };

  const handleCheckboxChange = (e) => {
    const newFilter = { ...filter, [e.target.name]: e.target.checked, from: 0 };
    setFilter(newFilter);
  };

  const handlePageClick = (e) => {
    const selectedPage = e.selected;
    const newOffset = (selectedPage - 1) * pageSize;
    if (newOffset !== filter.from) {
      navigate({ search: queryString.stringify({ ...filter, from: (selectedPage - 1) * pageSize }) });
    }
  };

  const setPageSizeClick = (pageSize) => {
    setPerPage(pageSize);
    navigate({ search: queryString.stringify({ ...filter, from: 0, pageSize }) });
  };

  const common = (
    <>
      <ProfileImportModal
        isOpen={importModalOpen}
        close={() => setImportModalOpen(false)}
        done={() => {
          setImportModalOpen(false);
          notifications.show({
            title: 'Import Scheduled',
            message:
              'Your import has been scheduled for processing. You will receive an e-mail confirmation once it has been processed.',
            color: 'success',
          });
        }}
      />
      <Card withBorder mb="sm">
        <Card.Section inheritPadding py="md" withBorder>
          <form onSubmit={(e) => search(e)}>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput name="profileId" label="Profile Id" value={filter.profileId || ''} onChange={handleChange} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput name="email" label="Email" value={filter.email || ''} onChange={handleChange} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput name="name" label="Name" value={filter.name || ''} onChange={handleChange} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  name="phoneNumber"
                  label="Phone Number"
                  value={filter.phoneNumber || ''}
                  onChange={handleChange}
                  placeholder={`+${countryPhoneCode}`}
                  error={phoneValidationError?.message}
                />
                {phoneValidationError && (
                  <Text size="xs" c="red" mt="xs">
                    <IconAlertCircle size={14} /> {phoneValidationError.message}
                  </Text>
                )}
              </Grid.Col>
            </Grid>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 10 }} ta="right" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Checkbox
                  name="showAnonymous"
                  label="Include Anonymous Profiles"
                  onChange={handleCheckboxChange}
                  checked={filter.showAnonymous}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 2 }} ta="right">
                <Button type="submit" disabled={!profiles}>
                  <IconSearch size={16} /> Go
                </Button>
              </Grid.Col>
            </Grid>
          </form>
        </Card.Section>
      </Card>
    </>
  );

  return (
    <>
      <Metadata title={`${Page['profiles']}`} />
      {common}
      <Card withBorder mb={10} data-tour={profiles ? 'profilesPageTable' : undefined}>
        <Card.Section inheritPadding py="md">
          <Flex justify="space-between" align="center">
            {totalCount !== undefined ? (
              <Title order={5}>
                <IconUser size={16} /> {totalCount.toLocaleString(navigator.language)} Profiles
              </Title>
            ) : (
              'Loading...'
            )}
            <Group gap="sm">
              <Button variant="default" disabled={!profiles} onClick={() => navigate(location.pathname + '/new')}>
                <IconPlus size={16} style={{ marginRight: 4 }} /> Add Profile
              </Button>
              <Button
                variant="default"
                disabled={!profiles}
                data-tour="importProfilesBtn"
                onClick={() => setImportModalOpen(true)}
              >
                <IconCloudUpload size={16} style={{ marginRight: 4 }} /> Import Profiles
              </Button>
            </Group>
          </Flex>
        </Card.Section>
        {profiles && (
          <Table striped withTableBorder highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Profile</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Phone number</Table.Th>
                <Table.Th>Registered At</Table.Th>
                <Table.Th>Subscribed At</Table.Th>
                <Table.Th>Last Change At</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <ProfileRows profiles={profiles} />
            </Table.Tbody>
          </Table>
        )}
      </Card>
      <Pager
        pageSize={pageSize}
        pageCount={pageCount}
        onPageChange={handlePageClick}
        initialPage={Math.round(parseInt(filter.from || '0') / pageSize + 1)}
        setPageSize={setPageSizeClick}
      />
    </>
  );
};

export default ProfilesPage;
